import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';
import { useLang } from '@/context/LanguageContext';

const SECTIONS = [
  { n: 1, title: 'terms.s1_t', paras: ['terms.s1_p'] },
  { n: 2, title: 'terms.s2_t', paras: ['terms.s2_p'] },
  { n: 3, title: 'terms.s3_t', items: [['terms.s3_a_t', 'terms.s3_a_p'], ['terms.s3_b_t', 'terms.s3_b_p'], ['terms.s3_c_t', 'terms.s3_c_p']] },
  { n: 4, title: 'terms.s4_t', paras: ['terms.s4_p'] },
  { n: 5, title: 'terms.s5_t', paras: ['terms.s5_p1', 'terms.s5_p2'], law: 'terms.s5_law' },
];

const TermsSection = ({ s, t }) => (
  <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-8" data-testid={`terms-section-${s.n}`}>
    <div className="flex items-center gap-3 mb-4">
      <span className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-sm font-bold text-[#D4AF37] flex-shrink-0">{s.n}</span>
      <h3 className="text-xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{t(s.title)}</h3>
    </div>
    {s.paras && (
      <div className="space-y-3">
        {s.paras.map(p => <p key={p} className="text-sm text-[#A3A19C] leading-relaxed">{t(p)}</p>)}
      </div>
    )}
    {s.items && (
      <div className="space-y-4">
        {s.items.map(([title, body]) => (
          <div key={title}>
            <p className="text-sm font-semibold text-[#E2B4BD] mb-1">{t(title)}</p>
            <p className="text-sm text-[#A3A19C] leading-relaxed">{t(body)}</p>
          </div>
        ))}
      </div>
    )}
    {s.law && (
      <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.06)]">
        <p className="text-xs text-[#D4AF37]">{t(s.law)}</p>
      </div>
    )}
  </div>
);

const TermsPage = () => {
  const { t } = useLang();
  return (
    <div className="pt-20 min-h-screen" data-testid="terms-page">
      <section className="py-16 lg:py-24 bg-[#121215] border-b border-[rgba(255,255,255,0.06)]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
              <p className="overline">{t('terms.overline')}</p>
            </div>
            <h1 className="text-4xl lg:text-5xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]" data-testid="terms-title">
              {t('terms.title')}
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <p className="text-[#E2B4BD] text-lg font-['Cormorant_Garamond'] italic leading-relaxed mb-12">{t('terms.intro')}</p>
            <div className="space-y-10">
              {SECTIONS.map(s => <TermsSection key={s.n} s={s} t={t} />)}
            </div>
            <div className="mt-12 text-center">
              <p className="text-[#E2B4BD] font-['Cormorant_Garamond'] text-lg italic">{t('terms.closing')}</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
